// HRM: Departments, Designations, Office Shifts, Employees, Attendance, Leaves, Holidays, Payroll
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Search, X, ArrowLeft, Save, Users, Building2,
  BadgeCheck, Clock, CalendarCheck, CalendarX, PartyPopper, Wallet, Check,
} from "lucide-react";
import { api } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader } from "../products/_shared";
import { fmtMoney as fmt } from "../../../lib/currency";

// ────────────────────────── HRM DASHBOARD ──────────────────────────
export function HRMDashboard() {
  const { subdomain } = useParams();
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/t/hrm/overview").then((r) => setStats(r.data)); }, []);
  const tiles = [
    { lbl: "Employees", v: stats.employees, icon: Users, to: "hrm/employees", color: "from-emerald-500 to-emerald-600" },
    { lbl: "Departments", v: stats.departments, icon: Building2, to: "hrm/departments", color: "from-sky-500 to-sky-600" },
    { lbl: "Designations", v: stats.designations, icon: BadgeCheck, to: "hrm/designations", color: "from-violet-500 to-violet-600" },
    { lbl: "Pending Leaves", v: stats.pending_leaves, icon: CalendarX, to: "hrm/leaves", color: "from-amber-500 to-amber-600" },
    { lbl: "Holidays", v: stats.holidays, icon: PartyPopper, to: "hrm/holidays", color: "from-pink-500 to-pink-600" },
    { lbl: "Pending Payrolls", v: stats.payrolls_pending, icon: Wallet, to: "hrm/payrolls", color: "from-indigo-500 to-indigo-600" },
  ];
  return (
    <div data-testid="hrm-dashboard-page" className="p-6 lg:p-8">
      <PageHeader title="Human Resources" breadcrumb="HRM / Dashboard" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.lbl} to={`/t/${subdomain}/${t.to}`}
              className="group bg-white rounded-2xl border border-black/[0.06] p-5 hover:border-[#1FA868]/40 transition-colors">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.color} text-white flex items-center justify-center`}><Icon size={18} /></div>
              <div className="mt-4 text-3xl font-extrabold tracking-tight text-black">{t.v ?? 0}</div>
              <div className="text-xs text-black/55 mt-1">{t.lbl}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────── Generic single-table CRUD ──────────────────────────
function SimpleTableCRUD({ title, breadcrumb, endpoint, fields, testidBase, columns, icon: Icon, onAfterChange }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    const blank = {};
    fields.forEach((f) => { blank[f.name] = f.default ?? ""; });
    setForm(blank); setEdit(null); setOpen(true);
  };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    try {
      if (edit) await api.patch(`${endpoint}/${edit.id}`, form);
      else await api.post(endpoint, form);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false); load(); onAfterChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    await api.delete(`${endpoint}/${r.id}`);
    toast.success("Deleted"); load(); onAfterChange?.();
  };

  const filtered = search ? rows.filter((r) => (r.name || "").toLowerCase().includes(search.toLowerCase())) : rows;

  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8">
      <PageHeader title={title} breadcrumb={breadcrumb} actions={
        <PrimaryButton icon={Plus} onClick={openCreate} testid={`${testidBase}-new`}>Create</PrimaryButton>
      } />
      <Card className="p-3 mb-4">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search this table"
            data-testid={`${testidBase}-search`} className="flex-1 bg-transparent focus:outline-none text-sm" />
        </div>
      </Card>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              {columns.map((c) => <th key={c.key} className="text-left p-3.5">{c.lbl}</th>)}
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                {columns.map((c) => (
                  <td key={c.key} className="p-3.5">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
                ))}
                <td className="p-3.5 text-right">
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-black/5 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={columns.length + 1} className="p-10 text-center text-black/45">No data for table</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <div className="flex items-center gap-2">
                {Icon && <Icon size={16} className="text-[#1FA868]" />}
                <h3 className="font-bold">{edit ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {fields.map((f) => (
                <Field key={f.name} lbl={f.lbl} required={f.required}>
                  {f.type === "select" ? (
                    <Select value={form[f.name] || ""} onChange={(v) => setForm((x) => ({ ...x, [f.name]: v }))} options={f.options || []} testid={`${testidBase}-f-${f.name}`} />
                  ) : f.type === "textarea" ? (
                    <textarea value={form[f.name] || ""} onChange={(e) => setForm((x) => ({ ...x, [f.name]: e.target.value }))}
                      data-testid={`${testidBase}-f-${f.name}`}
                      className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[80px]" />
                  ) : (
                    <TextInput type={f.type || "text"} value={form[f.name]} onChange={(v) => setForm((x) => ({ ...x, [f.name]: v }))} placeholder={f.placeholder} testid={`${testidBase}-f-${f.name}`} />
                  )}
                </Field>
              ))}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid={`${testidBase}-save`}>Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── Concrete HRM sub-pages ──────────────────────────
export function HRMDepartments() {
  return <SimpleTableCRUD
    title="Departments" breadcrumb="HRM / Departments" endpoint="/t/hrm/departments" testidBase="hrm-dept" icon={Building2}
    fields={[
      { name: "name", lbl: "Department", required: true, placeholder: "Engineering" },
      { name: "head", lbl: "Department Head" },
      { name: "description", lbl: "Description", type: "textarea" },
    ]}
    columns={[
      { key: "name", lbl: "Department", render: (r) => <span className="font-semibold">{r.name}</span> },
      { key: "head", lbl: "Department Head" },
      { key: "description", lbl: "Description" },
    ]} />;
}

export function HRMDesignations() {
  const [depts, setDepts] = useState([]);
  useEffect(() => { api.get("/t/hrm/departments").then((r) => setDepts(r.data)); }, []);
  return <SimpleTableCRUD
    title="Designations" breadcrumb="HRM / Designations" endpoint="/t/hrm/designations" testidBase="hrm-desig" icon={BadgeCheck}
    fields={[
      { name: "name", lbl: "Designation", required: true, placeholder: "Senior Developer" },
      { name: "department_id", lbl: "Department", type: "select", options: depts.map((d) => ({ value: d.id, label: d.name })) },
      { name: "description", lbl: "Description", type: "textarea" },
    ]}
    columns={[
      { key: "name", lbl: "Designation", render: (r) => <span className="font-semibold">{r.name}</span> },
      { key: "department_name", lbl: "Department" },
      { key: "description", lbl: "Description" },
    ]} />;
}

export function HRMShifts() {
  return <SimpleTableCRUD
    title="Office Shifts" breadcrumb="HRM / Office Shift" endpoint="/t/hrm/shifts" testidBase="hrm-shift" icon={Clock}
    fields={[
      { name: "name", lbl: "Shift Name", required: true, placeholder: "Day Shift" },
      { name: "start_time", lbl: "Start Time", type: "time", default: "09:00" },
      { name: "end_time", lbl: "End Time", type: "time", default: "18:00" },
    ]}
    columns={[
      { key: "name", lbl: "Shift", render: (r) => <span className="font-semibold">{r.name}</span> },
      { key: "start_time", lbl: "Start" },
      { key: "end_time", lbl: "End" },
    ]} />;
}

export function HRMHolidays() {
  return <SimpleTableCRUD
    title="Holidays" breadcrumb="HRM / Holidays" endpoint="/t/hrm/holidays" testidBase="hrm-hol" icon={PartyPopper}
    fields={[
      { name: "name", lbl: "Holiday", required: true, placeholder: "New Year's Day" },
      { name: "date", lbl: "Date", type: "date", required: true },
      { name: "description", lbl: "Description", type: "textarea" },
    ]}
    columns={[
      { key: "name", lbl: "Holiday", render: (r) => <span className="font-semibold">{r.name}</span> },
      { key: "date", lbl: "Date" },
      { key: "description", lbl: "Description" },
    ]} />;
}

// ────────────────────────── EMPLOYEES ──────────────────────────
export function HRMEmployees() {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const load = () => api.get("/t/hrm/employees").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/t/hrm/departments").then((r) => setDepts(r.data));
    api.get("/t/hrm/designations").then((r) => setDesigs(r.data));
    api.get("/t/hrm/shifts").then((r) => setShifts(r.data));
  }, []);

  const openCreate = () => { setForm({ first_name: "", last_name: "", email: "", phone: "", status: "active", salary: 0, date_joined: new Date().toISOString().slice(0, 10) }); setEdit(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    try {
      if (edit) await api.patch(`/t/hrm/employees/${edit.id}`, form);
      else await api.post("/t/hrm/employees", form);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    await api.delete(`/t/hrm/employees/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="hrm-emp-page" className="p-6 lg:p-8">
      <PageHeader title="Employees" breadcrumb="HRM / Employees" actions={
        <PrimaryButton icon={Plus} onClick={openCreate} testid="hrm-emp-new">Create</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Employee</th>
              <th className="text-left p-3.5">Department</th>
              <th className="text-left p-3.5">Designation</th>
              <th className="text-left p-3.5">Email</th>
              <th className="text-right p-3.5">Salary</th>
              <th className="text-left p-3.5">Status</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#E8F5E9] text-[#1FA868] font-bold text-xs flex items-center justify-center">{(r.name || "?").slice(0, 2).toUpperCase()}</div>
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-[11px] text-black/45">{r.phone || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3.5">{r.department_name || "—"}</td>
                <td className="p-3.5">{r.designation_name || "—"}</td>
                <td className="p-3.5">{r.email || "—"}</td>
                <td className="p-3.5 text-right font-semibold">{fmt(r.salary)}</td>
                <td className="p-3.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${r.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-black/5 text-black/60"}`}>{r.status}</span>
                </td>
                <td className="p-3.5 text-right">
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-black/5 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="p-10 text-center text-black/45">No employees yet</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">{edit ? "Edit" : "New"} Employee</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              <Field lbl="First Name" required><TextInput value={form.first_name} onChange={(v) => setForm((x) => ({ ...x, first_name: v }))} testid="hrm-emp-fn" /></Field>
              <Field lbl="Last Name" required><TextInput value={form.last_name} onChange={(v) => setForm((x) => ({ ...x, last_name: v }))} testid="hrm-emp-ln" /></Field>
              <Field lbl="Email"><TextInput value={form.email} onChange={(v) => setForm((x) => ({ ...x, email: v }))} testid="hrm-emp-email" /></Field>
              <Field lbl="Phone"><TextInput value={form.phone} onChange={(v) => setForm((x) => ({ ...x, phone: v }))} testid="hrm-emp-phone" /></Field>
              <Field lbl="Department"><Select value={form.department_id || ""} onChange={(v) => setForm((x) => ({ ...x, department_id: v }))} options={depts.map((d) => ({ value: d.id, label: d.name }))} /></Field>
              <Field lbl="Designation"><Select value={form.designation_id || ""} onChange={(v) => setForm((x) => ({ ...x, designation_id: v }))} options={desigs.map((d) => ({ value: d.id, label: d.name }))} /></Field>
              <Field lbl="Shift"><Select value={form.shift_id || ""} onChange={(v) => setForm((x) => ({ ...x, shift_id: v }))} options={shifts.map((s) => ({ value: s.id, label: s.name }))} /></Field>
              <Field lbl="Status"><Select value={form.status || "active"} onChange={(v) => setForm((x) => ({ ...x, status: v }))} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "terminated", label: "Terminated" }]} /></Field>
              <Field lbl="Salary"><TextInput type="number" value={form.salary} onChange={(v) => setForm((x) => ({ ...x, salary: Number(v) }))} testid="hrm-emp-sal" /></Field>
              <Field lbl="Date Joined"><TextInput type="date" value={form.date_joined} onChange={(v) => setForm((x) => ({ ...x, date_joined: v }))} /></Field>
              <Field lbl="Address" className="col-span-2">
                <textarea value={form.address || ""} onChange={(e) => setForm((x) => ({ ...x, address: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[60px]" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="hrm-emp-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── ATTENDANCE ──────────────────────────
export function HRMAttendance() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  useEffect(() => { api.get("/t/hrm/employees").then((r) => setEmployees(r.data)); }, []);
  useEffect(() => { api.get("/t/hrm/attendance", { params: { date } }).then((r) => setRecords(r.data)); }, [date]);
  const mark = async (employee_id, status) => {
    await api.post("/t/hrm/attendance", { employee_id, date, status });
    toast.success(`Marked ${status}`);
    api.get("/t/hrm/attendance", { params: { date } }).then((r) => setRecords(r.data));
  };
  const statusFor = (eid) => records.find((r) => r.employee_id === eid)?.status;
  const colorFor = (s) => ({ present: "bg-emerald-100 text-emerald-700", absent: "bg-rose-100 text-rose-700", late: "bg-amber-100 text-amber-700", "half-day": "bg-sky-100 text-sky-700", leave: "bg-violet-100 text-violet-700" })[s] || "bg-black/5 text-black/60";
  return (
    <div data-testid="hrm-att-page" className="p-6 lg:p-8">
      <PageHeader title="Attendance" breadcrumb="HRM / Attendance" actions={
        <div><TextInput type="date" value={date} onChange={setDate} testid="hrm-att-date" /></div>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Employee</th>
              <th className="text-left p-3.5">Department</th>
              <th className="text-left p-3.5">Status</th>
              <th className="text-right p-3.5">Mark</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {employees.map((e) => {
              const s = statusFor(e.id);
              return (
                <tr key={e.id}>
                  <td className="p-3.5 font-semibold">{e.name}</td>
                  <td className="p-3.5 text-black/70">{e.department_name || "—"}</td>
                  <td className="p-3.5">{s ? <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${colorFor(s)}`}>{s}</span> : <span className="text-black/45">Not marked</span>}</td>
                  <td className="p-3.5 text-right">
                    <div className="inline-flex gap-1">
                      {["present", "absent", "late", "half-day", "leave"].map((st) => (
                        <button key={st} onClick={() => mark(e.id, st)} data-testid={`hrm-att-${e.id}-${st}`}
                          className={`text-[10px] font-semibold px-2 py-1 rounded border ${s === st ? "bg-[#1FA868] text-white border-[#1FA868]" : "border-black/15 hover:bg-[#F4F5FA]"}`}>
                          {st[0].toUpperCase() + st.slice(1)}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!employees.length && <tr><td colSpan={4} className="p-10 text-center text-black/45">No employees — create employees first</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ────────────────────────── LEAVE REQUESTS ──────────────────────────
export function HRMLeaves() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = () => api.get("/t/hrm/leaves").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/hrm/employees").then((r) => setEmployees(r.data)); }, []);
  const submit = async () => {
    try {
      await api.post("/t/hrm/leaves", form);
      toast.success("Request submitted"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const review = async (r, status) => {
    await api.patch(`/t/hrm/leaves/${r.id}/review`, { status });
    toast.success(status); load();
  };
  const del = async (r) => {
    if (!window.confirm("Delete leave request?")) return;
    await api.delete(`/t/hrm/leaves/${r.id}`);
    toast.success("Deleted"); load();
  };
  const colorFor = (s) => ({ pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700" })[s];
  return (
    <div data-testid="hrm-leave-page" className="p-6 lg:p-8">
      <PageHeader title="Leave Requests" breadcrumb="HRM / Leave Request" actions={
        <PrimaryButton icon={Plus} onClick={() => { setForm({ leave_type: "annual", from_date: new Date().toISOString().slice(0, 10), to_date: new Date().toISOString().slice(0, 10) }); setOpen(true); }} testid="hrm-leave-new">New Request</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Employee</th>
              <th className="text-left p-3.5">Type</th>
              <th className="text-left p-3.5">From</th>
              <th className="text-left p-3.5">To</th>
              <th className="text-left p-3.5">Reason</th>
              <th className="text-left p-3.5">Status</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5 font-semibold">{r.employee_name || "—"}</td>
                <td className="p-3.5">{r.leave_type}</td>
                <td className="p-3.5">{r.from_date}</td>
                <td className="p-3.5">{r.to_date}</td>
                <td className="p-3.5 text-black/70 max-w-xs truncate">{r.reason || "—"}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${colorFor(r.status)}`}>{r.status}</span></td>
                <td className="p-3.5 text-right">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => review(r, "approved")} data-testid={`hrm-leave-approve-${r.id}`} className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-700 mr-1">Approve</button>
                      <button onClick={() => review(r, "rejected")} data-testid={`hrm-leave-reject-${r.id}`} className="text-[10px] font-semibold px-2 py-1 rounded bg-rose-100 text-rose-700 mr-1">Reject</button>
                    </>
                  )}
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="p-10 text-center text-black/45">No leave requests</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">New Leave Request</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field lbl="Employee" required><Select value={form.employee_id || ""} onChange={(v) => setForm((x) => ({ ...x, employee_id: v }))} options={employees.map((e) => ({ value: e.id, label: e.name }))} testid="hrm-leave-emp" /></Field>
              <Field lbl="Leave Type"><Select value={form.leave_type || "annual"} onChange={(v) => setForm((x) => ({ ...x, leave_type: v }))} options={["annual", "sick", "casual", "unpaid", "maternity"].map((t) => ({ value: t, label: t }))} /></Field>
              <Field lbl="From" required><TextInput type="date" value={form.from_date} onChange={(v) => setForm((x) => ({ ...x, from_date: v }))} testid="hrm-leave-from" /></Field>
              <Field lbl="To" required><TextInput type="date" value={form.to_date} onChange={(v) => setForm((x) => ({ ...x, to_date: v }))} testid="hrm-leave-to" /></Field>
              <Field lbl="Reason"><textarea value={form.reason || ""} onChange={(e) => setForm((x) => ({ ...x, reason: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="hrm-leave-save">Submit</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── PAYROLL ──────────────────────────
export function HRMPayroll() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = () => api.get("/t/hrm/payrolls").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/hrm/employees").then((r) => setEmployees(r.data)); }, []);
  const openCreate = () => {
    const period = new Date().toISOString().slice(0, 7);
    setForm({ period, basic: 0, allowances: 0, deductions: 0, bonus: 0, tax: 0 });
    setOpen(true);
  };
  const onEmployee = (id) => {
    const emp = employees.find((e) => e.id === id);
    setForm((f) => ({ ...f, employee_id: id, basic: emp?.salary || 0 }));
  };
  const submit = async () => {
    try {
      await api.post("/t/hrm/payrolls", form);
      toast.success("Payroll created"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pay = async (r) => {
    await api.patch(`/t/hrm/payrolls/${r.id}/pay`);
    toast.success("Marked paid"); load();
  };
  const del = async (r) => {
    if (!window.confirm("Delete payroll?")) return;
    await api.delete(`/t/hrm/payrolls/${r.id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="hrm-pay-page" className="p-6 lg:p-8">
      <PageHeader title="Payroll" breadcrumb="HRM / Payroll" actions={
        <PrimaryButton icon={Plus} onClick={openCreate} testid="hrm-pay-new">Generate</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Employee</th>
              <th className="text-left p-3.5">Period</th>
              <th className="text-right p-3.5">Basic</th>
              <th className="text-right p-3.5">Allowances</th>
              <th className="text-right p-3.5">Deductions</th>
              <th className="text-right p-3.5">Net</th>
              <th className="text-left p-3.5">Status</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5 font-semibold">{r.employee_name || "—"}</td>
                <td className="p-3.5">{r.period}</td>
                <td className="p-3.5 text-right">{fmt(r.basic)}</td>
                <td className="p-3.5 text-right">{fmt(r.allowances)}</td>
                <td className="p-3.5 text-right">{fmt(r.deductions)}</td>
                <td className="p-3.5 text-right font-bold text-[#1FA868]">{fmt(r.net)}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${r.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span></td>
                <td className="p-3.5 text-right">
                  {r.status !== "paid" && <button onClick={() => pay(r)} data-testid={`hrm-pay-pay-${r.id}`} className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-700 mr-1">Mark Paid</button>}
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="p-10 text-center text-black/45">No payroll entries</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">Generate Payroll</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Field lbl="Employee" required className="col-span-2"><Select value={form.employee_id || ""} onChange={onEmployee} options={employees.map((e) => ({ value: e.id, label: e.name }))} testid="hrm-pay-emp" /></Field>
              <Field lbl="Period (YYYY-MM)" required><TextInput value={form.period || ""} onChange={(v) => setForm((x) => ({ ...x, period: v }))} placeholder="2026-04" /></Field>
              <Field lbl="Basic"><TextInput type="number" value={form.basic} onChange={(v) => setForm((x) => ({ ...x, basic: Number(v) }))} /></Field>
              <Field lbl="Allowances"><TextInput type="number" value={form.allowances} onChange={(v) => setForm((x) => ({ ...x, allowances: Number(v) }))} /></Field>
              <Field lbl="Bonus"><TextInput type="number" value={form.bonus} onChange={(v) => setForm((x) => ({ ...x, bonus: Number(v) }))} /></Field>
              <Field lbl="Deductions"><TextInput type="number" value={form.deductions} onChange={(v) => setForm((x) => ({ ...x, deductions: Number(v) }))} /></Field>
              <Field lbl="Tax"><TextInput type="number" value={form.tax} onChange={(v) => setForm((x) => ({ ...x, tax: Number(v) }))} /></Field>
              <div className="col-span-2 bg-[#E8F5E9] rounded-lg p-3 text-sm">
                Net Salary: <span className="font-extrabold text-[#1FA868]">{fmt((form.basic || 0) + (form.allowances || 0) + (form.bonus || 0) - (form.deductions || 0) - (form.tax || 0))}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="hrm-pay-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
